import React, { useState } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useSelector } from "react-redux";
import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from "uuid";
import MDTypography from "components/MDTypography";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import _ from "lodash";
import { produce } from "immer";

const StopSelector = ({
  selectedJourney,
  onClearJourney,
  onBack,
  onEditStop,
  routeRange,
  setRouteRange,
  onJourneyUpdated,
  onHoverStop,
  // New props for Options functionality
  allJourneys,
  onImportStops,
  onGetClusteredStops,
}) => {

  // const { students = [] } = useSelector((state) => state.apiGetData || {});
  const apiState = useSelector((state) => state.apiGetData || {});
  const students = apiState.students || [];
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stopsDataSource, setStopsDataSource] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    filterId: null,
    name: "",
  });

  // --- Options State ---
  const [showOptions, setShowOptions] = useState(false);
  const [importMenuAnchor, setImportMenuAnchor] = useState(null);
  const [timeRange, setTimeRange] = useState({
    start: "", // e.g., "2024-01-01T00:00"
    end: "",
  });

  // -----------------------------------------------------------------
  // Helper – build payload for the API
  // -----------------------------------------------------------------
  const buildFullPayload = (journey) => {
    const payload = JSON.parse(JSON.stringify(journey));
    Object.values(payload.filters || {}).forEach((filter) => {
      filter.passengers = _.size(payload.stops?.[filter.filterId]) || 0;
      filter.selected = filter.passengers > 0;
    });
    return payload;
  };

  // -----------------------------------------------------------------
  // UPDATE whole journey
  // -----------------------------------------------------------------
  const handleUpdate = async () => {
    if (!selectedJourney) {
      toast.error("No journey selected");
      return;
    }

    try {
      const payload = buildFullPayload(selectedJourney);
      const sessionToken = localStorage.getItem("token");

      if (!sessionToken) {
        toast.error("Session token missing – please log in again");
        return;
      }

      const response = await fetch("https://v3.dhundhoo.com/journey/editStops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": sessionToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(`HTTP ${response.status}: ${err}`);
      }

      await response.json();
      toast.success("Journey updated successfully!");
      if (onJourneyUpdated) onJourneyUpdated(payload);
    } catch (err) {
      console.error("Update failed:", err);
      toast.error(`Update failed: ${err.message}`);
    }
  };

  // -----------------------------------------------------------------
  // UPDATE JOURNEY STOPS (NEW API)
  // -----------------------------------------------------------------
  const handleUpdateJourneyStops = async () => {
    if (!selectedJourney || !selectedJourney.sequence) {
      toast.error("No journey selected!");
      return;
    }

    // 1. Sequence ko order mein sort karein
    const sortedKeys = Object.keys(selectedJourney.sequence).sort(
      (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );

    // 2. Stops array banayein
    const stopsList = sortedKeys.map((key) => {
      const filterId = selectedJourney.sequence[key];
      const filter = selectedJourney.filters[filterId];
      return {
        stopName: filter?.tags?.[0] || "Unnamed Stop",
        latitude: parseFloat(filter?.data?.location?.latitude),
        longitude: parseFloat(filter?.data?.location?.longitude),
      };
    }).filter(s => !isNaN(s.latitude) && !isNaN(s.longitude));

    // 3. MINIMUM 2 STOPS VALIDATION
    if (stopsList.length < 2) {
      toast.error("Minimum 2 stops required (Source and Destination). Please add more stops on the map.");
      return;
    }

    // 4. Source (First Stop) aur Destination (Last Stop) nikaalein
    const source = {
      latitude: stopsList[0].latitude,
      longitude: stopsList[0].longitude,
    };

    const destination = {
      latitude: stopsList[stopsList.length - 1].latitude,
      longitude: stopsList[stopsList.length - 1].longitude,
    };

    // 5. Payload Format Ready Karein
    const payload = {
      journeyId: selectedJourney.journeyId,
      source: source,
      destination: destination,
      stops: stopsList // Saare stops intermediate list mein jayenge
    };

    try {
      const sessionToken = localStorage.getItem("token");
      const response = await fetch("https://v3.dhundhoo.com/journey/updateJourneyStops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": sessionToken,
        },
        body: JSON.stringify(payload),
      });

      // Agar response ok nahi hai toh text nikal ke error throw karein
      if (!response.ok) {
        const err = await response.text();
        throw new Error(`HTTP Error ${response.status}: ${err}`);
      }

      // JSON ki jagah text read karein
      const successMessage = await response.text();
      console.log("API Response:", successMessage);

      toast.success("Route generated and stops uploaded successfully!");

    } catch (err) {
      console.error("Update Route API Failed:", err);
      toast.error(`Failed to update route stops: ${err.message}`);
    }
  };

  // -----------------------------------------------------------------
  // REMOVE a stop
  // -----------------------------------------------------------------
  const removeStop = async (seq) => {
    const filterId = seq.filterId;

    try {
      const updatedJourney = produce(selectedJourney, (draft) => {
        Object.keys(draft.sequence || {}).forEach((key) => {
          if (draft.sequence[key] === filterId) delete draft.sequence[key];
        });

        if (draft.stops?.[filterId]) delete draft.stops[filterId];
        if (draft.filters?.[filterId]) delete draft.filters[filterId];

        const remainingIds = Object.values(draft.sequence);
        const newSeq = {};
        remainingIds.forEach((id, i) => {
          newSeq[i] = id;
        });
        draft.sequence = newSeq;

        if (Object.keys(draft.sequence).length === 0) {
          draft.sequence = {};
          draft.stops = {};
          draft.filters = {};
        }
      });

      const isLastStop = Object.keys(updatedJourney.sequence || {}).length === 0;

      if (isLastStop) {
        toast.success("Stop removed successfully!");
        if (onJourneyUpdated) onJourneyUpdated(updatedJourney);
        return;
      }

      const payload = buildFullPayload(updatedJourney);
      const sessionToken = localStorage.getItem("token");

      const response = await fetch("https://v3.dhundhoo.com/journey/editStops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": sessionToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(`HTTP ${response.status}: ${err}`);
      }

      await response.json();
      toast.success("Stop removed successfully!");
      if (onJourneyUpdated) onJourneyUpdated(updatedJourney);
    } catch (err) {
      console.error("Remove failed:", err);
      toast.error(`Failed to remove stop: ${err.message}`);
    }
  };

  // -----------------------------------------------------------------
  // Options Handlers
  // -----------------------------------------------------------------
  const handleOptionsToggle = () => setShowOptions((prev) => !prev);
  const handleImportClick = (event) => setImportMenuAnchor(event.currentTarget);
  const handleImportClose = () => setImportMenuAnchor(null);

  const handleImportJourney = (journey) => {
    handleImportClose(); // Menu band karein

    if (onImportStops) {
      // Parent ko source journey bhej rahe hain
      onImportStops(journey);
    }
  };

  const hoverCard = (seq) => console.log("Hover:", seq);
  const copyName = (seq) => {
    const name = selectedJourney.filters[seq.filterId].tags[0];
    navigator.clipboard.writeText(name);
    toast.success(`Copied: ${name}`);
  };

  const fixLocationFromName = (seq) => {
    const filterId = seq.filterId;
    const filter = selectedJourney.filters[filterId];

    // Stop ka naam nikalna
    let name = filter?.tags?.[0] || "";

    // Agar dusra tag zyaada bada/descriptive hai, toh usko use karein (Landmark)
    if (filter?.tags?.length > 1 && filter.tags[1].length > name.length) {
      name = filter.tags[1];
    }

    if (!name || name === "New Stop" || name === "Unnamed Stop") {
      toast.error("Valid stop name is required to fix location.");
      return;
    }

    toast.info(`Searching for exact location: ${name}...`);

    // Google Places API ko call karne ke liye ek dummy div aur map instance banana
    const dummyDiv = document.createElement("div");
    const map = new window.google.maps.Map(dummyDiv, {
      center: { lat: 0, lng: 0 },
      zoom: 15,
    });
    const service = new window.google.maps.places.PlacesService(map);

    const request = {
      query: name,
      fields: ["name", "geometry"],
    };

    // Google par naam search karna
    service.findPlaceFromQuery(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        const location = results[0].geometry.location;
        const newLat = location.lat();
        const newLng = location.lng();

        // Nayi coordinates ko sirf local state mein update karna (Immer ka use karke)
        const updatedJourney = produce(selectedJourney, (draft) => {
          if (draft.filters[filterId] && draft.filters[filterId].data && draft.filters[filterId].data.location) {
            draft.filters[filterId].data.location.latitude = newLat;
            draft.filters[filterId].data.location.longitude = newLng;
          }
        });

        toast.success(`Marker moved to exact location for ${name}! (Click Update to save)`);

        // Parent (Map) ko nayi state bhejna taaki marker instantly move ho jaye bina API hit ke
        if (onJourneyUpdated) {
          onJourneyUpdated(updatedJourney);
        }

      } else {
        toast.error(`Could not find exact coordinates for: ${name}`);
      }
    });
  };

  // Pehle ye function add karein jo CSV ko JSON me convert karega
  const onUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target.result;
      // XLSX read binary string format mein
      const workbook = XLSX.read(data, { type: 'binary' });
      let dataStops = [];

      workbook.SheetNames.forEach((sheetName) => {
        const dataJson = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        // Aapke template ka sheet name 'AddStops' hai
        if (sheetName === 'AddStops' || sheetName === 'Sheet1') {
          dataStops = dataJson;
        }
      });

      const formattedJourney = createStopsData(dataStops);
      if (formattedJourney) {
        setStopsDataSource(formattedJourney);
        setSelectedFile(file);
        toast.success("File processed successfully!");
      }
    };
    reader.readAsBinaryString(file);
  };

  const createStopsData = (datas) => {
    let journey = JSON.parse(JSON.stringify(selectedJourney));
    let invalid = '';

    if (!journey.filters) journey.filters = {};
    if (!journey.stops) journey.stops = {};
    if (!journey.sequence) journey.sequence = {};

    datas.forEach((json, index) => {
      const dataRow = index + 1;
      const stopName = json['Stop Name'];
      const admissionNoField = json['Admission No'];

      if (!stopName) {
        invalid += `Row ${dataRow}: Stop Name missing; `; return;
      }
      if (!admissionNoField) {
        invalid += `Row ${dataRow}: Admission No missing; `; return;
      }

      const filterId = uuidv4();
      const lat = json['Latitude'] || '17.450640';
      const lng = json['Longitude'] || '78.382830';

      // 1. Filter Object
      journey.filters[filterId] = {
        filterId: filterId,
        tags: [stopName, json['Landmark'] || ""],
        type: "LocationFilter",
        data: {
          type: json['Stop Type'] || "BUS_STOP",
          location: { latitude: parseFloat(lat), longitude: parseFloat(lng) }
        },
        adminOnly: false
      };

      // const admissionNos = String(admissionNoField).split(',').map(s => s.trim());
      // journey.stops[filterId] = admissionNos;
      const admissionNos = String(admissionNoField).split(',').map(s => s.trim());

      journey.stops[filterId] = admissionNos.map((adm) => {
        const matchedStudent = students.find((s) => {
          const rollFromAccountId = s.accountId?.includes("::")
            ? s.accountId.split("::")[1]
            : null;
          return (
            rollFromAccountId === adm ||
            s.extra?.rollNumber === adm ||
            s.accountId === adm
          );
        });
        return matchedStudent ? matchedStudent.accountId : adm;
      });

      const seqSize = Object.keys(journey.sequence).length;
      journey.sequence[seqSize] = filterId;
    });

    if (invalid.length > 0) {
      toast.error(`Invalid data: ${invalid}`);
      return null;
    }

    return journey;
  };

  const handleBulkUploadSubmit = async () => {
    if (!stopsDataSource) {
      toast.error("Please upload and process a file first.");
      return;
    }

    // Final payload matching your console log requirements
    const payload = {
      ...stopsDataSource,
      stopsMarked: true, // Kyunki ab stops add ho chuke hain
    };

    try {
      const response = await fetch("https://v3.dhundhoo.com/journey/editStops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": localStorage.getItem("token") || "session",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Bulk Upload Successful!");
        setIsBulkModalOpen(false);
        setStopsDataSource(null);
        if (onJourneyUpdated) onJourneyUpdated(payload);
      } else {
        const errText = await response.text();
        toast.error(`Upload failed: ${errText}`);
      }
    } catch (error) {
      toast.error("Network error during upload");
    }
  };

  const handleDownloadTemplate = () => {
    // Aapki uploaded file ke basis pe exact headers
    const headers = ["Stop Name", "Stop Type", "Latitude", "Longitude", "Landmark", "Admission No"];

    // Sample data (optional)
    const sampleData = ["Sample Stop", "BUS_STOP", "17.4586", "78.4057", "Landmark Name", "SR/23-24/00001"];

    const csvContent = [
      headers.join(","),
      sampleData.join(",")
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Stops_Upload_Template.csv");
    link.click();
  };


  const handleDownloadStops = () => {
    if (!selectedJourney || !selectedJourney.sequence) {
      toast.error("No journey data to download");
      return;
    }

    const headers = [
      "Stop Name",
      "Stop Type",
      "Latitude",
      "Longitude",
      "Landmark",
      "Admission No",
      "Student Names"
    ];

    const sortedKeys = Object.keys(selectedJourney.sequence).sort(
      (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );

    const rows = sortedKeys.map((key) => {
      const filterId = selectedJourney.sequence[key];
      const filter = selectedJourney.filters[filterId];
      const assignedStudentStrings = selectedJourney.stops?.[filterId] || [];

      const admissionNosList = [];
      const studentNamesList = [];

      assignedStudentStrings.forEach(str => {
        // str format: "UUID::Roll" (e.g. "dac48...::GVIS25/00001")
        // Student API response accountId format: "UUID::Roll" 

        // 1. Sidha poora string match karke dekhein (kya pata ID exact store ho)
        let studentDetail = students.find(s => s.accountId === str);

        // 2. Agar match nahi mila, toh parts split karke try karein
        if (!studentDetail && str.includes("::")) {
          const parts = str.split("::");
          const rollNumberFromStop = parts[1]; // GVIS25/00001

          studentDetail = students.find(s => {
            // Check if accountId ends with this roll number or contains it
            return s.accountId.includes(rollNumberFromStop) || s.accountId === str;
          });
        }

        if (studentDetail) {
          // Roll number hum parts[1] se ya accountId ke suffix se nikaal sakte hain
          const roll = str.includes("::") ? str.split("::")[1] : studentDetail.extra?.rollNumber || str;
          const name = `${studentDetail.firstName || ""} ${studentDetail.lastName || ""}`.trim();

          admissionNosList.push(roll);
          studentNamesList.push(name);
        } else {
          // Fallback: Agar student list mein nahi mila toh stop wali info hi dikha dein
          const fallbackRoll = str.includes("::") ? str.split("::")[1] : str;
          admissionNosList.push(fallbackRoll);
          studentNamesList.push("Not Registered");
        }
      });

      return [
        filter?.tags?.[0] || "Unnamed Stop",
        filter?.data?.type || "BUS_STOP",
        filter?.data?.location?.latitude || "",
        filter?.data?.location?.longitude || "",
        filter?.tags?.[1] || "none",
        admissionNosList.join(", "),
        studentNamesList.join(", ")
      ];
    });

    // --- Construct CSV (Aapka existing logic) ---
    const csvContent = [
      headers.map(h => `"${h}"`).join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob(["\ufeff", csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `AllStopsData_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV Downloaded with Student Names");
  };

  // const handleDownloadStops = () => {
  //   if (!selectedJourney || !selectedJourney.sequence) {
  //     toast.error("No journey data to download");
  //     return;
  //   }

  //   // CSV Headers
  //   const headers = [
  //     "Stop Name",
  //     "Stop Type",
  //     "Latitude",
  //     "Longitude",
  //     "Landmark",
  //     "Admission No",
  //     "Students Name"
  //   ];

  //   // Sort sequence keys to maintain stop order
  //   const sortedKeys = Object.keys(selectedJourney.sequence).sort(
  //     (a, b) => parseInt(a, 10) - parseInt(b, 10)
  //   );

  //   const rows = sortedKeys.map((key) => {
  //     const filterId = selectedJourney.sequence[key];
  //     const filter = selectedJourney.filters[filterId];
  //     const assignedStudentStrings = selectedJourney.stops?.[filterId] || [];

  //     // Map assigned student IDs to their details
  //     const stopStudents = assignedStudentStrings.map(str => {
  //       const studentId = str.split("::")[0];
  //       return students.find(s => s.accountId === studentId);
  //     }).filter(Boolean);

  //     console.log("stops student",stopStudents);


  //     // Join multiple students with commas (as seen in your example snippet)
  //     const admissionNos = stopStudents.map(s => s.extra?.rollNumber || "").join(", ");
  //     const studentNames = stopStudents.map(s => `${s.firstName} ${s.lastName}`).join(", ");

  //     return [
  //       filter?.tags?.[0] || "",
  //       filter?.data?.type || "BUS_STOP",
  //       filter?.data?.location?.latitude || "",
  //       filter?.data?.location?.longitude || "",
  //       filter?.tags?.[1] || "none",
  //       admissionNos,
  //       studentNames
  //     ];
  //   });

  //   // Construct CSV String
  //   const csvContent = [
  //     headers.map(h => `"${h}"`).join(","),
  //     ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
  //   ].join("\n");

  //   // Create Download Link
  //   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  //   const url = URL.createObjectURL(blob);
  //   const link = document.createElement("a");
  //   const filename = `AllStopsData_${new Date().toISOString().split('T')[0]}.csv`;

  //   link.setAttribute("href", url);
  //   link.setAttribute("download", filename);
  //   link.style.visibility = "hidden";
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);

  //   toast.success("CSV Downloaded Successfully");
  // };

  const handleBulkUpload = () => toast.info("Bulk upload initiated");

  const sortedSequenceKeys = Object.keys(selectedJourney?.sequence || {}).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );

  const stops = sortedSequenceKeys.map((seqKey, idx) => {
    const filterId = selectedJourney.sequence[seqKey];
    return {
      filterId,
      index: idx + 1,
    };
  });


  return (
    <MDBox
      sx={{
        width: "500px",
        height: "100%",
        boxShadow: "2px 0 6px rgba(0,0,0,0.1)",
        overflowY: "auto",
        backgroundColor: "#f5f7fa",
        p: 2,
        borderRadius: '24px'
      }}
    >
      {/* ------------------- HEADER CARD ------------------- */}
      <MDBox sx={{ mb: 2 }}>
        <Card sx={{ p: 1, borderRadius: 5, backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <CardContent sx={{ p: 1 }}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item>
                <Typography variant="body2">{selectedJourney?.thingId ?? "-"}</Typography>
              </Grid>
              <Grid item>
                <MDButton variant="contained" color="warning" size="small" onClick={handleDownloadStops} sx={{ mr: 1 }}>
                  Download Stops
                </MDButton>
                {/* </Grid> */}
                {/* <Grid item> */}
                <MDButton variant="contained" color="warning" size="small" onClick={() => setIsBulkModalOpen(true)}>
                  Bulk Upload
                </MDButton>
              </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {selectedJourney?.type || "Test PICKING"}
            </Typography>

            <div style={{ marginTop: "12px", width: "100%" }}>
              <input
                type="range"
                min="0"
                max="100"
                value={routeRange[1]}
                onChange={(e) => setRouteRange([0, Number(e.target.value)])}
                style={{
                  width: "100%",
                  cursor: "pointer",
                  accentColor: "#00bcd4",
                  margin: 0,
                  height: "4px",
                }}
                title="Drag to adjust route length"
              />
            </div>

            <Grid
              container
              spacing={1}
              sx={{
                mt: 2,
                justifyContent: "space-between",
                flexWrap: "nowrap", // Ye sabko ek row me force karega
                overflowX: "auto" // Agar space kam padi toh scroll ho jayega, line break nahi hogi
              }}
            >
              <Grid item>
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={onBack}
                  sx={{ px: 1.5 }} // Padding thodi adjust ki hai taaki fit ho
                >
                  Back
                </MDButton>
              </Grid>
              <Grid item>
                <MDButton
                  variant={showOptions ? "contained" : "outlined"}
                  color="warning"
                  onClick={handleOptionsToggle}
                  sx={{ px: 1.5 }}
                >
                  Options
                </MDButton>
              </Grid>
              <Grid item>
                <MDButton
                  variant="contained"
                  color="warning"
                  onClick={handleUpdate}
                  sx={{ px: 1.5 }}
                >
                  Update
                </MDButton>
              </Grid>
              <Grid item>
                <MDButton
                  variant="contained"
                  color="warning"
                  onClick={handleUpdateJourneyStops}
                  sx={{ px: 1.5, whiteSpace: "nowrap" }} // Text ko break hone se rokega
                >
                  Generate Route
                </MDButton>
              </Grid>
            </Grid>

            {/* ------------------- EXPANDABLE OPTIONS SECTION ------------------- */}
            <Collapse in={showOptions}>
              <MDBox mt={2} pt={2} sx={{ borderTop: "1px solid #e0e0e0" }}>
                <Grid container spacing={2}>
                  {/* Import Stops Button & Menu */}
                  <Grid item xs={12}>
                    <MDButton variant="contained" color="warning" fullWidth onClick={handleImportClick}>
                      Import Stops
                    </MDButton>
                    <Menu
                      anchorEl={importMenuAnchor}
                      open={Boolean(importMenuAnchor)}
                      onClose={handleImportClose}
                      PaperProps={{ style: { maxHeight: 300, width: "300px" } }}
                    >
                      {allJourneys && allJourneys.length > 0 ? (
                        allJourneys.map((item) => (
                          <MenuItem key={item.journeyId || item.id} onClick={() => handleImportJourney(item)}>
                            {`${item.journeyName || "Unknown"} ${item.type || ""}`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No journeys available</MenuItem>
                      )}
                    </Menu>
                  </Grid>
                </Grid>
              </MDBox>
            </Collapse>
          </CardContent>
        </Card>
      </MDBox>

      {/* ------------------- STOP LIST OR EMPTY MESSAGE ------------------- */}
      <MDBox>
        {stops.length === 0 ? (
          <MDBox p={3} textAlign="center" color="text.secondary">
            <Typography variant="body2">
              No stops added yet. <br />
              Click anywhere on the map to create the first stop.
            </Typography>
          </MDBox>
        ) : (
          stops.map((seq) => (
            <Card
              key={seq.filterId}
              onMouseEnter={() => {
                hoverCard(seq);
                if (onHoverStop) onHoverStop(seq.filterId);
              }}
              onMouseLeave={() => {
                if (onHoverStop) onHoverStop(null);
              }}
              sx={{
                mb: 2,
                backgroundColor: selectedJourney.filters[seq.filterId]?.selected ? "#f5f5f5" : "#fff",
                transition: "all 0.3s ease", // <-- Smooth animation ke liye
                transform: "scale(1)",
                borderLeft: "4px solid transparent",
                "&:hover": {
                  boxShadow: "0 8px 16px rgba(0, 188, 212, 0.3)", // <-- Glow effect
                  transform: "translateY(-2px)", // <-- Thoda upar uthne ka effect
                  borderLeft: "4px solid #00bcd4", // <-- Left side me indicator
                  zIndex: 1
                },
              }}
            >
              <CardContent sx={{ p: "10px !important" }}>
                <Grid
                  container
                  alignItems="center"
                  onClick={() => copyName(seq)}
                  sx={{
                    cursor: "pointer",
                    mb: 1,
                    p: 1,
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                  }}
                >
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      {seq.index}. {selectedJourney.filters[seq.filterId]?.tags[0]}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">
                      Passengers: {_.size(selectedJourney.stops?.[seq.filterId])}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="body2" sx={{ textAlign: "left", ml: 0.5, mt: 1 }}>
                  Landmark -{" "}
                  {selectedJourney.filters[seq.filterId].tags.length > 1
                    ? selectedJourney.filters[seq.filterId].tags[1]
                    : "none"}
                </Typography>

                <Grid container spacing={1} sx={{ mt: 2 }}>
                  <Grid item xs={4}>
                    <MDButton
                      variant="gradient"
                      color="warning"
                      fullWidth
                      onClick={() => {
                        const filter = selectedJourney.filters[seq.filterId];
                        const stop = {
                          filterId: seq.filterId,
                          name: filter.tags[0],
                          type: filter.data?.type || "BUS_STOP",
                          latitude: filter.data?.location?.latitude,
                          longitude: filter.data?.location?.longitude,
                          externalHandle: filter.externalHandle,
                          // students: (selectedJourney.stops?.[seq.filterId] || []).map((s) => s.split("::")[0]),
                          students: selectedJourney.stops?.[seq.filterId] || []
                        };
                        onEditStop(stop);
                      }}
                    >
                      Edit
                    </MDButton>
                  </Grid>
                  <Grid item xs={4}>
                    <MDButton variant="gradient" color="warning" fullWidth onClick={() => fixLocationFromName(seq)}>
                      Fix
                    </MDButton>
                  </Grid>
                  <Grid item xs={4}>
                    <MDButton
                      variant="gradient"
                      color="warning"
                      fullWidth
                      onClick={() => {
                        setDeleteModal({
                          open: true,
                          filterId: seq.filterId,
                          name: selectedJourney.filters[seq.filterId].tags[0],
                        });
                      }}
                    >
                      Remove
                    </MDButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))
        )}
      </MDBox>

      <Dialog open={isBulkModalOpen} onClose={() => setIsBulkModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Stops</DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            <MDTypography variant="body2" mb={2}>
              Please use our template to ensure the data is in the correct format.
            </MDTypography>

            <MDButton
              variant="outlined"
              color="info"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 3 }}
            >
              Download Template (CSV)
            </MDButton>

            {/* <input
              type="file"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px dashed #ccc",
                borderRadius: "4px"
              }}
            /> */}
            {selectedFile && (
              <MDTypography variant="caption" color="success" sx={{ mt: 1, display: "block" }}>
                Selected: {selectedFile.name}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setIsBulkModalOpen(false)} color="inherit">
            Cancel
          </MDButton>
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            onChange={onUpload} // File select hote hi processing shuru
            style={{
              width: "100%",
              padding: "10px",
              border: "1px dashed #ccc",
              borderRadius: "4px"
            }}
          />

          {/* Button click par ab processed data API pe jayega */}
          <MDButton
            onClick={handleBulkUploadSubmit}
            variant="contained"
            color="info"
            disabled={!stopsDataSource}
          >
            Upload & Process
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ------------------- DELETE CONFIRMATION DIALOG ------------------- */}
      <Dialog
        open={deleteModal.open}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Stop</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteModal.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteModal({ ...deleteModal, open: false })} color="inherit">
            Cancel
          </MDButton>
          <MDButton
            color="error"
            variant="contained"
            onClick={async () => {
              setDeleteModal({ ...deleteModal, open: false });
              await removeStop({ filterId: deleteModal.filterId });
            }}
          >
            Remove
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

StopSelector.propTypes = {
  selectedJourney: PropTypes.object,
  onClearJourney: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onEditStop: PropTypes.func.isRequired,
  onJourneyUpdated: PropTypes.func,
  allJourneys: PropTypes.array,
  onImportStops: PropTypes.func,
  onGetClusteredStops: PropTypes.func,
};

StopSelector.defaultProps = {
  onClearJourney: () => { },
  onJourneyUpdated: () => { },
  allJourneys: [],
};

export default StopSelector;